// Alert sources (#422): where a workspace's alerts come in, and whether they do.
//
// A source's status is the receiver's own record: waiting until the customer's
// subscription confirms, confirmed after, and the deliveries since. The secret a signing
// sender needs is shown once, on creation, and never again.
//
// "Send test alert" (#876; #795 decided it) sends a sample alarm through the source's own
// parser and opens an investigation marked as a test, which costs no credits. It is not a
// delivery: a source still goes green only when the customer's own sender reaches it.

import { readingAsOrg, type OrgKind } from "../api/memberships";
import { useEffect, useRef, useState, type FormEvent } from "react";

import type { AutomationRead } from "../api/automation";
import { utcTime } from "../api/automation";
import { messageOf } from "../api/http";
import type { SessionResponse } from "../api/session";
import {
  DEFAULT_DRY_RUN_DAYS,
  MAX_DRY_RUN_DAYS,
  SOURCE_TYPES,
  type FilterSettings,
  type NewSource,
  type Setup,
  type SourceFilterRead,
  type SourceRow,
  type SourceType,
  type TestAlertSent,
} from "../api/sources";
import { AutomaticInvestigations } from "./AutomaticInvestigations";
import { Chip, Empty, Field, Notice, PageIntro } from "./chrome";
import { runPagePath } from "../runId";
import { destinationOf } from "./routes";
import { useDropOnPageHide } from "./pageHide";
import { useReadOnly } from "./readOnly";
import { SetupTrail, inFirstRun } from "./SetupTrail";
import { SourceFilter } from "./SourceFilter";

const STATUS: Record<SourceRow["status"], { label: string; tone?: "good" | "bad" | "warn" }> = {
  waiting: { label: "Waiting for a subscription", tone: "warn" },
  confirmed: { label: "Confirmed", tone: "good" },
  disabled: { label: "Disabled", tone: "bad" },
};

function sourceStatus(source: SourceRow) {
  const status = STATUS[source.status];
  return source.status === "waiting" && source.source_type !== "cloudwatch_sns"
    ? { ...status, label: "Waiting for first delivery" }
    : status;
}

export function SourcesPage({
  session,
  orgKind = null,
  sources,
  error,
  onCreate,
  onSetActive,
  onSetAutoInvestigate,
  onDelete,
  onSendTestAlert,
  onReload,
  onReadFilter,
  onSaveFilter,
  onSetDryRun,
  automation,
  setup = null,
}: {
  session: SessionResponse;
  /** What is left before the first investigation, or null while unread (#805). */
  setup?: Setup | null;
  sources: SourceRow[] | null;
  error: string | null;
  onCreate: (body: NewSource) => Promise<SourceRow>;
  onSetActive: (id: string, active: boolean) => Promise<void>;
  /** Whether an alarm on one source starts an investigation (#816). */
  onSetAutoInvestigate?: (id: string, on: boolean) => Promise<void>;
  /** Delete one source (#816): its endpoint then refuses alerts. */
  onDelete?: (id: string) => Promise<void>;
  /** Send one source's sample alarm (#876); answers the test's investigation. */
  onSendTestAlert?: (id: string) => Promise<TestAlertSent>;
  onReload: () => void;
  /** Which alarms start an investigation on one source (#847). */
  onReadFilter?: (id: string) => Promise<SourceFilterRead>;
  onSaveFilter?: (id: string, settings: FilterSettings) => Promise<SourceFilterRead>;
  /** Start or end one source's dry run (#849). */
  onSetDryRun?: (id: string, days: number) => Promise<SourceFilterRead>;
  /** The daily limit on automatic investigations and the alarms waiting past it (#849). */
  automation?: {
    read: () => Promise<AutomationRead>;
    saveCap: (dailyCap: number | null) => Promise<AutomationRead>;
  };
  /** The kind of the org this context reads under, for the org-context empty state (#1205). */
  orgKind?: OrgKind | null;
}) {
  const [created, setCreated] = useState<SourceRow | null>(null);
  // The add form is open while the workspace has no source; once it has one, it waits
  // behind a button (#816).
  const [adding, setAdding] = useState(false);
  const [deleted, setDeleted] = useState<string | null>(null);
  const readOnly = useReadOnly();
  // After an act that removes the control a person pressed, focus goes where they can go on.
  const addButton = useRef<HTMLButtonElement>(null);
  const listHeading = useRef<HTMLHeadingElement>(null);
  const [refocus, setRefocus] = useState<{ to: "add" | "list"; n: number } | null>(null);
  useEffect(() => {
    if (refocus === null) return;
    (refocus.to === "add" ? addButton.current : listHeading.current)?.focus();
  }, [refocus]);
  const focusOn = (to: "add" | "list") => setRefocus((was) => ({ to, n: (was?.n ?? 0) + 1 }));
  if (session.workspace_id === null) {
    return (
      <>
        <PageIntro destination="sources" />
        <section className="panel">
          <Empty title="Alert sources belong to a workspace">
            <p>{readingAsOrg(orgKind, "see where its alerts come in")}</p>
          </Empty>
        </section>
      </>
    );
  }
  return (
    <>
      <PageIntro
        destination="sources"
        action={<button type="button" className="btn" onClick={onReload}>Refresh</button>}
      />
      <SetupTrail current="sources" setup={setup} />
      {error === null ? null : <Notice tone="bad">{error} Refresh to try again.</Notice>}
      {inFirstRun(setup) && sources?.some((source) => source.is_active && source.status === "confirmed") ? (
        <section className="setup-next" aria-label="Alert source confirmed">
          <div><h2>Your alert source is confirmed</h2><p className="muted">Choose the model provider your investigations will use.</p></div>
          <a className="btn btn--primary" href={destinationOf("provider").path}>Continue to model provider</a>
        </section>
      ) : null}
      {readOnly ? null : created !== null ? (
        <Instructions
          source={{ ...created, ...sources?.find((source) => source.id === created.id), secret: created.secret }}
          onDone={() => {
            setCreated(null);
            focusOn("add");
          }}
        />
      ) : sources !== null && sources.length === 0 ? (
        <AddSource onCreate={async (body) => setCreated(await onCreate(body))} />
      ) : adding ? (
        <AddSource
          focusOnOpen
          onCreate={async (body) => {
            setCreated(await onCreate(body));
            setAdding(false);
          }}
          onCancel={() => {
            setAdding(false);
            focusOn("add");
          }}
        />
      ) : (
        <div className="actions">
          <button
            type="button"
            className="btn btn--primary"
            ref={addButton}
            onClick={() => {
              setDeleted(null);
              setAdding(true);
            }}
          >
            Add an alert source
          </button>
        </div>
      )}
      {automation === undefined ? null : (
        <AutomaticInvestigations
          readOnly={readOnly}
          onRead={automation.read}
          onSaveCap={automation.saveCap}
        />
      )}
      <section className="panel" data-testid="sources">
        <div className="panel__head">
          <h2 ref={listHeading} tabIndex={-1}>
            Your sources
          </h2>
          {sources === null ? null : <Chip>{sources.length} shown</Chip>}
        </div>
        {deleted === null ? null : (
          <div className="panel__body">
            <Notice tone="good">{deleted} was deleted. Alerts sent to its endpoint are now refused.</Notice>
          </div>
        )}
        {sources === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading this workspace's sources…
          </p>
        ) : null}
        {sources !== null && sources.length === 0 ? (
          <Empty title="No alert source yet" mark="○">
            <p>Add one above. A CloudWatch alarm topic is the usual first.</p>
          </Empty>
        ) : null}
        {sources !== null && sources.length > 0 ? (
          <ul className="list" aria-label="Alert sources">
            {sources.map((source) => (
              <li key={source.id} className="panel__body stack setup-source-card" data-testid="source">
                <div className="row row--between row--wrap">
                  <div>
                    <h3>{source.name}</h3>
                    <small className="muted">
                      {[
                        SOURCE_TYPES.find(([t]) => t === source.source_type)?.[1] ?? source.source_type,
                        source.description.trim(),
                      ]
                        .filter((part) => part !== "")
                        .join(" · ")}
                    </small>
                    <p>
                      <code className="break">{source.url}</code>
                    </p>
                  </div>
                  <Chip tone={sourceStatus(source).tone}>{sourceStatus(source).label}</Chip>
                </div>
                <div className="row row--wrap">
                  {source.confirmed_accounts.length > 0 ? (
                    <Chip tone={source.status === "confirmed" && source.is_active ? "good" : undefined}>
                      subscribed from {source.confirmed_accounts.join(", ")}
                    </Chip>
                  ) : null}
                  <Chip>
                    {source.alert_count} alert{source.alert_count === 1 ? "" : "s"}
                    {source.last_alert_at ? `, last ${source.last_alert_at.slice(0, 16).replace("T", " ")}` : ""}
                  </Chip>
                  {source.refused > 0 ? <Chip tone="bad">{source.refused} refused</Chip> : null}
                  {source.refused_by_spend ? (
                    // One count over every reason the gate gives (#1354); the Overview names it.
                    <Chip tone="bad">{source.refused_by_spend} not investigated: credits or billing</Chip>
                  ) : null}
                  {source.skipped ? <Chip>{source.skipped} skipped by the filter</Chip> : null}
                  {source.dry_run_until ? (
                    <Chip tone="warn">In a dry run until {utcTime(source.dry_run_until)}</Chip>
                  ) : null}
                </div>
                {source.status === "waiting" && source.source_type === "cloudwatch_sns" ? (
                  <p className="muted">
                    Subscribe a topic in a connected account to this URL; SNS confirms it within a
                    minute. Refresh to read the confirmation. The first alarm on that topic opens the first
                    investigation.
                  </p>
                ) : null}
                {source.status === "disabled" ? <p className="muted">This source is disabled. Enable it to accept alerts again.</p> : null}
                {source.status === "confirmed" && source.alert_count === 0 ? <p className="muted">Waiting for the first alert. When your monitoring sends an alarm, its investigation appears in Investigations.</p> : null}
                {onSetAutoInvestigate === undefined ? null : (
                  <AutoInvestigate source={source} readOnly={readOnly} onSet={onSetAutoInvestigate} />
                )}
                <SourceActions
                  source={source}
                  readOnly={readOnly}
                  onSetActive={onSetActive}
                  onDelete={onDelete}
                  onDeleted={() => {
                    setDeleted(source.name);
                    focusOn("list");
                  }}
                />
                {readOnly || onSendTestAlert === undefined || !source.is_active ? null : (
                  <SendTestAlert sourceId={source.id} onSend={onSendTestAlert} />
                )}
                {onReadFilter === undefined || onSaveFilter === undefined ? null : (
                  <SourceFilter
                    sourceId={source.id}
                    readOnly={readOnly}
                    onRead={onReadFilter}
                    onSave={onSaveFilter}
                    onSetDryRun={onSetDryRun}
                  />
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
}

function AddSource({
  onCreate,
  onCancel,
  focusOnOpen = false,
}: {
  onCreate: (body: NewSource) => Promise<void>;
  /** Fold the form back behind its button; absent while the form is the only way in. */
  onCancel?: () => void;
  /** Opened by a person's press: the first field takes focus. */
  focusOnOpen?: boolean;
}) {

  const [name, setName] = useState("");
  const [kind, setKind] = useState<SourceType>("cloudwatch_sns");
  // Offered at setup (#849): a week's dry run, ticked, so the filter can be tuned first.
  const [dryRun, setDryRun] = useState(true);
  const [days, setDays] = useState(String(DEFAULT_DRY_RUN_DAYS));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onCreate({ name, source_type: kind, dry_run_days: dryRun ? Number(days) : 0 });
      setName("");
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  const chosen = SOURCE_TYPES.find(([t]) => t === kind);
  return (
    <section className="panel setup-source-create" data-testid="add-source">
      <div className="panel__head">
        <h2>Add an alert source</h2>
      </div>
      <form className="panel__body" onSubmit={submit} aria-busy={busy}>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <Field
          id="source-name"
          autoFocus={focusOnOpen}
          label="Name"
          name="name"
          required
          placeholder="Production alarms"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <p className="field">
          <label htmlFor="source-type" className="field__label">
            Kind
          </label>
          <select
            id="source-type"
            className="input"
            value={kind}
            onChange={(event) => setKind(event.target.value as SourceType)}
          >
            {SOURCE_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {chosen ? <small className="field__hint">{chosen[2]}</small> : null}
        </p>
        <label className="checkline">
          <input type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
          <span>
            Start with a dry run{" "}
            <small className="field__hint">
              Alarms that would start an investigation are listed on the source and start nothing, so you can tune
              which alarms start one before switching it on.
            </small>
          </span>
        </label>
        {dryRun ? (
          <p className="field">
            <label htmlFor="source-dry-run-days" className="field__label">
              Dry run days
            </label>
            <input
              id="source-dry-run-days"
              className="input input--inline"
              type="number"
              min={1}
              max={MAX_DRY_RUN_DAYS}
              required
              value={days}
              onChange={(event) => setDays(event.target.value)}
            />
          </p>
        ) : null}
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Creating…" : "Create the source"}
          </button>
          {onCancel === undefined ? null : (
            <button type="button" className="btn" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/** What to do next, once, with the secret where there is one. */
function Instructions({ source, onDone }: { source: SourceRow; onDone: () => void }) {
  // Leaving the page puts the secret away as Done does (#929).
  useDropOnPageHide(onDone);
  return (
    <section className="panel panel--attention" data-testid="instructions">
      <div className="panel__head">
        <h2>Connect {source.name}</h2>
        <Chip tone={sourceStatus(source).tone}>{sourceStatus(source).label}</Chip>
      </div>
      <div className="panel__body stack">
        <div className="setup-endpoint"><span className="field__label">Your source endpoint</span><code className="break">{source.url}</code><CopyValue value={source.url} label="Copy endpoint" /></div>
        {source.source_type === "cloudwatch_sns" ? (
          <ol className="facts">
            <li>
              In the connected AWS account, open SNS and create a topic, or use the one your
              CloudWatch alarms already publish to.
            </li>
            <li>
              Add a subscription to that topic: protocol <strong>HTTPS</strong>, endpoint{" "}
              <code className="break">{source.url}</code>.
            </li>
            <li>
              SNS sends a confirmation to that URL. Torque Assist confirms it for you, because the
              topic is in an account this workspace connected. Refresh this page to read the confirmation.
            </li>
            <li>
              Point your alarms' actions at the topic.{" "}
              {source.dry_run_until
                ? "While the dry run lasts, an alarm that would open an investigation is listed on the source instead."
                : "The first alarm that fires opens your first investigation."}
            </li>
          </ol>
        ) : (
          <>
            <p>
              Send to <code className="break">{source.url}</code>, signing each request with the
              secret below. It is shown once; store it in your sender's configuration now.
            </p>
            {source.secret ? <div className="setup-endpoint">
              <span className="field__label">Secret · shown once</span>
              <code className="break">{source.secret}</code>
              <CopyValue value={source.secret} label="Copy secret" />
            </div> : <Notice tone="bad">The creation response did not include a signing secret. Create a new source to obtain one.</Notice>}
          </>
        )}
        <div className="actions">
          <button type="button" className="btn" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    </section>
  );
}

/** Clipboard feedback stays local; neither endpoints nor one-time secrets are cached. */
function CopyValue({ value, label }: { value: string; label: string }) {
  const [result, setResult] = useState<"copied" | "failed" | null>(null);
  const copy = async () => {
    setResult(null);
    try {
      await navigator.clipboard.writeText(value);
      setResult("copied");
    } catch {
      setResult("failed");
    }
  };
  return (
    <>
      <button type="button" className="btn btn--small" onClick={() => void copy()}>
        {label}
      </button>
      {result === "copied" ? <span role="status">Copied</span> : null}
      {result === "failed" ? (
        <span role="alert">Copy unavailable. Select and copy the value above.</span>
      ) : null}
    </>
  );
}

function SourceToggle({ source, onSetActive }: {
  source: SourceRow;
  onSetActive: (id: string, active: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSetActive(source.id, !source.is_active);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        className={source.is_active ? "btn btn--small btn--danger" : "btn btn--small"}
        disabled={busy}
        onClick={() => void toggle()}
      >
        {busy ? "Updating…" : source.is_active ? "Disable" : "Enable"}
      </button>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
    </>
  );
}

/** Whether an alarm on this source starts an investigation (#816). Off, alarms still arrive
 * and are kept on the source's alerts, and none starts one. */
function AutoInvestigate({
  source,
  readOnly,
  onSet,
}: {
  source: SourceRow;
  readOnly: boolean;
  onSet: (id: string, on: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await onSet(source.id, on);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <label className="checkline">
        <input
          type="checkbox"
          checked={source.auto_investigate}
          disabled={readOnly || busy}
          onChange={(event) => void change(event.target.checked)}
        />
        <span>
          Start an investigation for each alarm{" "}
          <small className="field__hint">
            When this is off, alarms still arrive and are listed with the source's alerts, and none starts an
            investigation.
          </small>
        </span>
      </label>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
    </>
  );
}

/** "Send test alert" (#876): the source's sample alarm, and where its investigation is. */
function SendTestAlert({
  sourceId,
  onSend,
}: {
  sourceId: string;
  onSend: (id: string) => Promise<TestAlertSent>;
}) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<TestAlertSent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      setSent(await onSend(sourceId));
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack stack--tight">
      <div className="actions">
        {/* Unavailable while in flight, not disabled: a disabled button drops the focus of the
            person who pressed it from the keyboard. */}
        <button type="button" className="btn btn--small" aria-disabled={busy} onClick={() => void send()}>
          {busy ? "Sending…" : "Send test alert"}
        </button>
      </div>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {sent === null ? null : (
        <Notice tone="good">
          {sent.joined
            ? "Test alert sent. It joined the investigation an earlier test opened"
            : "Test alert sent. Its investigation is marked as a test and costs no credits"}
          {sent.run_id === null ? (
            "."
          ) : (
            <>
              : <a href={runPagePath(sent.run_id)}>Open the investigation</a>
            </>
          )}
        </Notice>
      )}
    </div>
  );
}

/** A row's buttons: copy, disable or enable, and delete after a confirmation that says
 * what stops (#816). */
function SourceActions({
  source,
  readOnly,
  onSetActive,
  onDelete,
  onDeleted,
}: {
  source: SourceRow;
  readOnly: boolean;
  onSetActive: (id: string, active: boolean) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  /** The row is about to leave the list: the page says so and takes focus. */
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const [closed, setClosed] = useState(0);
  useEffect(() => {
    if (confirming) keep.current?.focus();
  }, [confirming]);
  useEffect(() => {
    if (closed > 0) opener.current?.focus();
  }, [closed]);
  const cancel = () => {
    setConfirming(false);
    setError(null);
    setClosed((n) => n + 1);
  };
  const remove = async () => {
    if (onDelete === undefined) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(source.id);
      onDeleted();
    } catch (failure) {
      setError(`${messageOf(failure)} The source was not deleted.`);
      setBusy(false);
    }
  };
  const titleId = `delete-source-${source.id}`;
  const actions = (
    <div className="actions">
      <CopyValue value={source.url} label="Copy endpoint" />
      {readOnly ? null : <SourceToggle source={source} onSetActive={onSetActive} />}
      {readOnly || onDelete === undefined || confirming ? null : (
        <button type="button" className="btn btn--small btn--danger" ref={opener} onClick={() => setConfirming(true)}>
          Delete
        </button>
      )}
    </div>
  );
  if (!confirming) return actions;
  return (
    <>
      {actions}
      <div
        className="stack stack--tight"
        role="group"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) cancel();
        }}
      >
        <p id={titleId}>
          <strong>Delete {source.name}?</strong>
        </p>
        <small className="field__hint">
          Alerts sent to its endpoint will be refused, and it leaves this list. Alarms it has already sent keep
          their source.
        </small>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <div className="actions">
          <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={() => void remove()}>
            {busy ? "Deleting…" : "Delete the source"}
          </button>
          <button type="button" className="btn btn--small" ref={keep} disabled={busy} onClick={cancel}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
